import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import AppState from '@webapp/store/state';

import { RecordLabelState } from '../../../store/record-labels/state';
import { RecordLabelRelation } from '@common/types/record-label/record-label-relation';
import { UpdateRecordLabelName } from '@webapp/store/record-labels/action-creators';
import {
  DeleteRecordLabel,
  RestoreRecordLabel,
  RemoveFromRecordLabel,
  UpdateRelationSplit,
} from '../../../store/record-labels/action-creators';

import styled, { css, useTheme } from 'styled-components';
import CircleCheckIcon from '@components/basics/icons/circle-check';
import UnselectedIcon from '@components/basics/icons/unselected';
import { SubscriptionState } from '../../../store/subscription/state';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import { ListReleasesArtist } from '@webapp/store/release/action-creators';
import { LIST_RELEASES_ARTIST } from '@webapp/store/release/actions';
import Avatar from '@components/basics/avatar';
import Flex from '@components/layout/flex';
import { AnimatePresence } from 'framer-motion';
import MotionOpacity from '@components/motion/motion-opacity';
import Typography from '@components/basics/typography';

interface LabelBoxProps {
  className?: string;
  label: RecordLabelRelation;
  relations: RecordLabelState['artists'];
  artistLabels: RecordLabelRelation[];
  subscriptions: SubscriptionState['label'];
  isLoadingList: boolean;
  isExternal: boolean;
  updateRecordLabelName: (
    labelId: string,
    name: string
  ) => UpdateRecordLabelName;
  removeRecordLabel: (labelId: string) => DeleteRecordLabel;
  restoreRecordLabel: (
    labelId: string,
    artists: string[]
  ) => RestoreRecordLabel;
  removeArtist: (relation: RecordLabelRelation) => RemoveFromRecordLabel;
  updateRelationSplit: (
    relation: RecordLabelRelation,
    split: number
  ) => UpdateRelationSplit;
  gotoDeleteLabel: () => CallHistoryMethodAction;
  ListReleasesArtist: (artistId: string) => ListReleasesArtist;
  releases: any[];
  toggleCheck: (id: string) => void;
}

const mapStateToProps = (state: AppState) => {
  return {
    releases: state.release.listArtist,
    relations: state.recordLabels.artists,
    artistLabels: state.recordLabels.member,
    operators: state.recordLabels.operators,
    subscriptions: state.subscription.label,
    isLoadingList: !!state.ui.loading[LIST_RELEASES_ARTIST],
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateRecordLabelName: (labelId: string, name: string) =>
    dispatch(new UpdateRecordLabelName(labelId, name)),
  removeRecordLabel: (labelId: string) =>
    dispatch(new DeleteRecordLabel(labelId)),
  restoreRecordLabel: (labelId: string, artists: string[]) =>
    dispatch(new RestoreRecordLabel(labelId, artists)),
  removeArtist: (relation: RecordLabelRelation) =>
    dispatch(new RemoveFromRecordLabel(relation)),
  updateRelationSplit: (relation: RecordLabelRelation, split: number) =>
    dispatch(new UpdateRelationSplit(relation, split)),
  gotoDeleteLabel: () => dispatch(push('/plans-record-label')),
  ListReleasesArtist: (artistId: string) =>
    dispatch(new ListReleasesArtist(artistId)),
});

export const LabelBox: React.FC<LabelBoxProps> = (props) => {
  const { toggleCheck, label } = props;
  const theme = useTheme();

  const CheckboxTitle = styled(Typography)<{ isNew?: boolean }>`
    margin-right: 10px;
    ${({ isNew }) =>
      isNew
        ? css`
            display: none;
          `
        : null}
  `;

  const getBgColor = () => {
    if (label.isChecked) {
      return theme.colors.primary;
    } else {
      return theme.colors.background;
    }
  };

  const getBgColorInv = () => {
    if (label.isChecked) {
      return theme.colors.background;
    } else {
      return theme.colors.primary;
    }
  };

  const rightDecoration = (
    <Flex justifyContent="flex-end" alignItems="center">
      <AnimatePresence>
        {!label.isChecked ? (
          <MotionOpacity>
            <CheckboxTitle
              weight="700"
              uppercase
              isNew={label.isChecked}
            ></CheckboxTitle>
          </MotionOpacity>
        ) : null}
      </AnimatePresence>
      {label.isChecked ? (
        <>
          <CircleCheckIcon
            onClick={() => toggleCheck(label.id)}
            dark
            className="check-icon"
            iconSize={24}
          />
        </>
      ) : (
        <UnselectedIcon
          onClick={() => toggleCheck(label.id)}
          className="check-icon"
          iconSize={24}
        />
      )}
    </Flex>
  );

  return (
    <>
      <StyledContainer style={{ background: getBgColor() }}>
        <Wrapper>
          <FlexRow>
            <ImageDiv>
              <Avatar src={label.artistAvatar ? label.artistAvatar : null} />
            </ImageDiv>
            <EmptyDiv>
              <TextDiv style={{ color: getBgColorInv() }}>
                {label.artistName}
              </TextDiv>
              <TextReleases style={{ color: getBgColorInv() }}>
                {label.artistReleases || 0} releases
              </TextReleases>
            </EmptyDiv>
          </FlexRow>

          <RightDiv>{rightDecoration}</RightDiv>
        </Wrapper>
      </StyledContainer>
    </>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(LabelBox)`
  margin-bottom: 20px;
  @media ${({ theme }) => theme.device.tablet} {
    margin-bottom: 30px;
  }
  &:last-child {
    margin-bottom: 0;
  }
`);

const EmptyDiv = styled.div``;

const RightDiv = styled.div`
  margin-right: 10px;
`;

const TextDiv = styled.div`
  color: ${({ theme }) => theme.colors.background};
`;

const TextReleases = styled.div`
  font-size: 12px;
  opacity: 0.7;
`;

const ImageDiv = styled.div`
  margin-right: 10px;
  margin-left: 10px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;
const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StyledContainer = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
  border-radius: 2px;
  display: flex;
  height: 60px;
  background-color: ${({ theme }) => theme.colors.primary};
`;
